import { useState, useEffect, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function MessagingPanel({ admin }: { admin: any }) {
  const [providers, setProviders] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [contactType, setContactType] = useState<'provider' | 'client'>('provider');
  const [messages, setMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadContacts();
  }, [contactType]);

  useEffect(() => {
    if (selectedContact) {
      loadMessages(selectedContact.id);
      
      // Subscribe to new messages
      const channel = supabase
        .channel('admin_messages_' + selectedContact.id)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'admin_messages',
            filter: `conversation_id=eq.${selectedContact.id}`
          },
          (payload) => {
            setMessages(prev => [...prev, payload.new]);
            scrollToBottom();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [selectedContact]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadContacts = async () => {
    if (contactType === 'provider') {
      const { data } = await supabase
        .from('provider_profiles')
        .select('*')
        .order('full_name');

      if (data) {
        // Load unread counts
        const contactsWithUnread = await Promise.all(
          data.map(async (contact) => {
            const { count } = await supabase
              .from('admin_messages')
              .select('*', { count: 'exact', head: true })
              .eq('conversation_id', contact.id)
              .eq('recipient_type', 'admin')
              .eq('is_read', false);

            return { ...contact, unreadCount: count || 0, type: 'provider' };
          })
        );

        setProviders(contactsWithUnread);
      }
    } else {
      const { data } = await supabase
        .from('users')
        .select('*')
        .order('full_name');

      if (data) {
        // Load unread counts
        const contactsWithUnread = await Promise.all(
          data.map(async (contact) => {
            const { count } = await supabase
              .from('admin_messages')
              .select('*', { count: 'exact', head: true })
              .eq('conversation_id', contact.id)
              .eq('recipient_type', 'admin')
              .eq('is_read', false);

            return { ...contact, unreadCount: count || 0, type: 'client' };
          })
        );

        setClients(contactsWithUnread);
      }
    }
  };

  const loadMessages = async (contactId: string) => {
    const { data } = await supabase
      .from('admin_messages')
      .select('*')
      .eq('conversation_id', contactId)
      .order('created_at', { ascending: true });

    if (data) {
      setMessages(data);
      
      // Mark messages as read
      await supabase
        .from('admin_messages')
        .update({ is_read: true })
        .eq('conversation_id', contactId)
        .eq('recipient_type', 'admin')
        .eq('is_read', false);

      // Reload contacts to update unread counts
      loadContacts();
    }
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedContact) return;

    setLoading(true);

    const { data: { user } } = await supabase.auth.getUser();
    
    const { error } = await supabase
      .from('admin_messages')
      .insert([{
        conversation_id: selectedContact.id,
        sender_id: user?.id,
        sender_type: 'admin',
        recipient_id: selectedContact.user_id || selectedContact.id,
        recipient_type: selectedContact.type,
        message: newMessage
      }]);

    if (!error) {
      setNewMessage('');
    }

    setLoading(false);
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + ' ' + 
             date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    }
  };

  const currentContacts = contactType === 'provider' ? providers : clients;
  const filteredContacts = currentContacts.filter(contact =>
    contact.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    contact.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalUnread = currentContacts.reduce((sum, c) => sum + c.unreadCount, 0);

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Messaging Center</h2>
        <p className="text-gray-600 mt-1">Communicate with providers and clients</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <i className="ri-message-3-line text-xl text-red-600 w-5 h-5 flex items-center justify-center"></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{totalUnread}</p>
              <p className="text-xs text-gray-600">Unread Messages</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-user-settings-line text-xl text-blue-600 w-5 h-5 flex items-center justify-center"></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{providers.length}</p>
              <p className="text-xs text-gray-600">Providers</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <i className="ri-user-line text-xl text-green-600 w-5 h-5 flex items-center justify-center"></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{clients.length}</p>
              <p className="text-xs text-gray-600">Clients</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <i className="ri-chat-check-line text-xl text-purple-600 w-5 h-5 flex items-center justify-center"></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{messages.length}</p>
              <p className="text-xs text-gray-600">Total Messages</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden" style={{ height: 'calc(100vh - 400px)' }}>
        <div className="flex h-full">
          {/* Contacts List */}
          <div className="w-80 border-r border-gray-200 flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <div className="flex space-x-2 mb-3">
                <button
                  onClick={() => {
                    setContactType('provider');
                    setSelectedContact(null);
                  }}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    contactType === 'provider'
                      ? 'bg-[#0B3C5D] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Providers ({providers.length})
                </button>
                <button
                  onClick={() => {
                    setContactType('client');
                    setSelectedContact(null);
                  }}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    contactType === 'client'
                      ? 'bg-[#0B3C5D] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Clients ({clients.length})
                </button>
              </div>
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 flex items-center justify-center"></i>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B3C5D] focus:border-transparent text-sm"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-gray-200">
              {filteredContacts.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <i className="ri-inbox-line text-4xl text-gray-300 mb-2 w-10 h-10 flex items-center justify-center mx-auto"></i>
                  <p className="text-sm">No {contactType}s found</p>
                </div>
              ) : (
                filteredContacts.map((contact) => (
                  <button
                    key={contact.id}
                    onClick={() => setSelectedContact(contact)}
                    className={`w-full p-4 text-left hover:bg-gray-50 transition-colors cursor-pointer ${
                      selectedContact?.id === contact.id ? 'bg-blue-50 border-l-4 border-[#0B3C5D]' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                          contactType === 'provider' ? 'bg-blue-600' : 'bg-green-600'
                        }`}>
                          {contact.full_name?.[0]?.toUpperCase() || 'U'}
                        </div>
                        {contact.unreadCount > 0 && (
                          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                            {contact.unreadCount}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{contact.full_name || 'Unknown'}</p>
                        <p className="text-sm text-gray-500 truncate">{contact.email}</p>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 flex flex-col">
            {selectedContact ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-blue-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                        contactType === 'provider' ? 'bg-blue-600' : 'bg-green-600'
                      }`}>
                        {selectedContact.full_name?.[0]?.toUpperCase() || 'U'}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{selectedContact.full_name || 'Unknown'}</p>
                        <p className="text-sm text-gray-600">{selectedContact.email}</p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                      contactType === 'provider' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {contactType === 'provider' ? 'Provider' : 'Client'}
                    </span>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                  {messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                      <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                        <i className="ri-chat-3-line text-4xl text-blue-600 w-10 h-10 flex items-center justify-center"></i>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">No Messages Yet</h3>
                      <p className="text-gray-600 max-w-md">
                        Start the conversation by sending a message to {selectedContact.full_name || 'this user'}
                      </p>
                    </div>
                  ) : (
                    <>
                      {messages.map((message) => (
                        <div
                          key={message.id}
                          className={`flex ${message.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`flex items-end space-x-2 max-w-md ${message.sender_type === 'admin' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                            {message.sender_type !== 'admin' && (
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white flex-shrink-0 font-semibold text-sm ${
                                contactType === 'provider' ? 'bg-blue-600' : 'bg-green-600'
                              }`}>
                                {selectedContact.full_name?.[0]?.toUpperCase() || 'U'}
                              </div>
                            )}
                            <div>
                              <div
                                className={`px-4 py-2 rounded-2xl ${
                                  message.sender_type === 'admin'
                                    ? 'bg-[#0B3C5D] text-white rounded-br-none'
                                    : 'bg-white text-gray-900 border border-gray-200 rounded-bl-none'
                                }`}
                              >
                                <p className="text-sm">{message.message}</p>
                              </div>
                              <p className={`text-xs mt-1 ${
                                message.sender_type === 'admin' ? 'text-right text-gray-500' : 'text-left text-gray-500'
                              }`}>
                                {formatTime(message.created_at)}
                              </p>
                            </div>
                            {message.sender_type === 'admin' && (
                              <div className="w-8 h-8 bg-[#0B3C5D] rounded-full flex items-center justify-center text-white flex-shrink-0">
                                <i className="ri-shield-user-line text-sm w-4 h-4 flex items-center justify-center"></i>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </div>

                {/* Message Input */}
                <form onSubmit={sendMessage} className="p-4 border-t border-gray-200 bg-white">
                  <div className="flex space-x-3">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-[#0B3C5D] focus:border-transparent text-sm"
                    />
                    <button
                      type="submit"
                      disabled={loading || !newMessage.trim()}
                      className="w-12 h-12 bg-[#0B3C5D] text-white rounded-full hover:bg-[#0B3C5D]/90 transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transform hover:scale-105"
                    >
                      <i className="ri-send-plane-fill text-xl w-5 h-5 flex items-center justify-center"></i>
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <i className="ri-message-3-line text-6xl text-gray-300 mb-4 w-16 h-16 flex items-center justify-center mx-auto"></i>
                  <p className="text-lg font-semibold text-gray-900 mb-2">Select a Contact</p>
                  <p className="text-gray-600">Choose a {contactType} from the list to start messaging</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
